import type { ReactNode } from 'react';
import type { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';
import { Checkbox } from '../../../components/ui/Checkbox';
import { FormField, FormSection } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { numberFieldOptions, textFieldOptions } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { type FieldVisibility, type RunFieldGroup, visibleGroups } from '../run-fields';

export interface RunFieldGroupsProps<TValues extends FieldValues> {
  idPrefix: string;
  groups: readonly RunFieldGroup<TValues>[];
  visibility: FieldVisibility;
  register: UseFormRegister<TValues>;
  errors: FieldErrors<TValues>;
  /** Extra context under a group's heading — the vehicle's placard tire size, for instance. */
  hints?: Partial<Record<string, ReactNode>>;
}

/**
 * Renders the shared field descriptors as a form. Both wizard steps are built from this, so the pre-test
 * checklist and the measurement sheet stay laid out and labelled identically to the run detail page.
 */
export const RunFieldGroups = <TValues extends FieldValues>({
  idPrefix,
  groups,
  visibility,
  register,
  errors,
  hints,
}: RunFieldGroupsProps<TValues>) => {
  const { t } = useI18nTranslation();
  // `FieldErrors` is a deeply-typed mapped type; the descriptors only ever address top-level scalar fields.
  const messageFor = (name: string): string | undefined => (errors as Record<string, { message?: string } | undefined>)[name]?.message;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {visibleGroups(groups, visibility).map((group) => (
        <section
          key={group.titleKey}
          className="rounded-lg border border-border bg-card p-5"
        >
          <FormSection title={t(group.titleKey)}>
            {hints?.[group.titleKey] ? <p className="text-xs text-muted-foreground">{hints[group.titleKey]}</p> : null}

            <div className="grid gap-3 sm:grid-cols-2">
              {group.fields.map((field) => {
                const id = `${idPrefix}-${field.name}`;
                const error = messageFor(field.name);

                if (field.kind === 'boolean') {
                  return (
                    <div
                      key={field.name}
                      className="flex items-end pb-1.5"
                    >
                      <Checkbox
                        id={id}
                        label={t(field.labelKey)}
                        {...register(field.name as Path<TValues>)}
                      />
                    </div>
                  );
                }

                return (
                  <FormField
                    key={field.name}
                    id={id}
                    label={field.unit ? `${t(field.labelKey)} (${field.unit})` : t(field.labelKey)}
                    error={error}
                  >
                    {field.kind === 'select' ? (
                      <Select
                        id={id}
                        // A blank first option, because every condition is optional: "not recorded" has to
                        // stay a choice the engineer can make and go back to.
                        placeholder={t('common.notRecorded')}
                        options={(field.options ?? []).map((option) => ({ value: option, label: t(`${field.optionLabelPrefix ?? ''}.${option}`) }))}
                        hasError={Boolean(error)}
                        {...register(field.name as Path<TValues>, textFieldOptions)}
                      />
                    ) : (
                      <Input
                        id={id}
                        type={field.kind === 'number' ? 'number' : 'text'}
                        inputMode={field.kind === 'number' ? 'decimal' : undefined}
                        step={field.step}
                        hasError={Boolean(error)}
                        {...register(field.name as Path<TValues>, field.kind === 'number' ? numberFieldOptions : textFieldOptions)}
                      />
                    )}
                  </FormField>
                );
              })}
            </div>
          </FormSection>
        </section>
      ))}
    </div>
  );
};
